import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { API_CONFIG, ApiConfig } from '../../api-config';
import { ApiResponse } from '@shared-models';

interface PageableData {
  content: any[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  first: boolean;
  last: boolean;
  empty: boolean;
}

export interface ServiceStatus {
  id: string;
  code: string;
  name: string;
  view: boolean;
  status: string;
}

export interface ServiceStatusPageResponse {
  items: ServiceStatus[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

@Injectable({ providedIn: 'root' })
export class ServiceStatusService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);

  private get apiUrl(): string {
    return `${this.apiConfig.apiUrl}/settings/services`;
  }

  /**
   * GET /api/settings/services/{serviceId}/status
   * Lista de estatus de un servicio (paginación server-side)
   */
  getStatuses(
    serviceId: string,
    page = 0,
    size = 20,
    sort = 'createdAt',
    direction: 'asc' | 'desc' = 'desc',
    search?: string,
    status?: string,
  ): Observable<ServiceStatusPageResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString())
      .set('sort', sort)
      .set('direction', direction);

    if (search && search.trim()) {
      params = params.set('search', search.trim());
    }
    if (status && status.trim()) {
      params = params.set('status', status.trim());
    }

    const url = `${this.apiUrl}/${serviceId}/status`;
    return this.httpClient.get<ApiResponse<PageableData>>(url, { params }).pipe(
      map((response) => {
        const pageableData = response.data;
        const items = pageableData?.content || [];
        return {
          items: items.map((it: any) => ({
            id: it.id || '',
            code: it.code || '',
            name: it.name || '',
            view: it.view ?? false,
            status: (it.status || 'ACTIVE').toUpperCase(),
          })) as ServiceStatus[],
          page: pageableData?.page ?? 0,
          size: pageableData?.size ?? size,
          totalElements: pageableData?.totalElements ?? 0,
          totalPages: pageableData?.totalPages ?? 0,
        } as ServiceStatusPageResponse;
      }),
    );
  }

  /**
   * POST /api/settings/services/{serviceId}/status
   */
  create(
    serviceId: string,
    payload: { code: string; name: string; view: boolean },
  ): Observable<{ message: string }> {
    const url = `${this.apiUrl}/${serviceId}/status`;
    return this.httpClient
      .post<{ success: boolean; message: string }>(url, payload)
      .pipe(
        map((res) => ({
          message: res.message || 'Estatus creado',
        })),
      );
  }

  /**
   * PUT /api/settings/services/{serviceId}/status/{statusId}
   */
  update(
    serviceId: string,
    statusId: string,
    payload: { code: string; name: string; view: boolean },
  ): Observable<{ message: string }> {
    const url = `${this.apiUrl}/${serviceId}/status/${statusId}`;
    return this.httpClient
      .put<{ success: boolean; message: string }>(url, payload)
      .pipe(
        map((res) => ({
          message: res.message || 'Estatus actualizado',
        })),
      );
  }

  /**
   * PATCH /api/settings/services/{serviceId}/status/{statusId}/status
   * Cambia el estatus (ACTIVE/INACTIVE) de un registro
   */
  toggleStatus(
    serviceId: string,
    statusId: string,
  ): Observable<{ message: string }> {
    const url = `${this.apiUrl}/${serviceId}/status/${statusId}/status`;
    return this.httpClient
      .patch<{ success: boolean; message: string }>(url, {})
      .pipe(
        map((res) => ({
          message: res.message || 'Estatus cambiado',
        })),
      );
  }

  /**
   * DELETE /api/settings/services/{serviceId}/status/{statusId}
   */
  delete(serviceId: string, statusId: string): Observable<{ message: string }> {
    const url = `${this.apiUrl}/${serviceId}/status/${statusId}`;
    return this.httpClient
      .delete<{ success: boolean; message: string }>(url)
      .pipe(
        map((res) => ({
          message: res.message || 'Estatus eliminado',
        })),
      );
  }
}
